/*
 * ===============================================================================================================
 *                                Copyright 2022, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import CopyToClipboard from 'react-copy-to-clipboard';
import { Box, Typography, Tooltip } from '@mui/material';
import { Copy, Eye, EyeOff } from '@jda/lui-common-icon-library-mui5';

import { useStyles } from './styles';
import interpolateString from '../../utility/stringInterpolation';
import { MESSAGES, SNACKBAR_ALERT_DEFAULT_PROPS } from './constant';
import { setSnackBarMessage } from '../../store/snackBar/snackBarActionCreator';

interface IUserPasswordDisplay {
  passwordText: string;
  userName: string;
}

const UserPasswordDisplay = ({ passwordText, userName }: IUserPasswordDisplay) => {
  const { classes } = useStyles();
  const dispatch = useDispatch();

  const [showUserPassword, setShowUserPassword] = useState(false);

  const displayUserPasswordTextHandler = () => {
    return showUserPassword ? passwordText : '************************';
  };

  const showUserPasswordHandler = () => {
    setShowUserPassword(true);
  };

  const hideUserPasswordHandler = () => {
    setShowUserPassword(false);
  };

  const handleCopyClick = () => {
    dispatch(
      setSnackBarMessage({
        ...SNACKBAR_ALERT_DEFAULT_PROPS,
        message: MESSAGES.PASSWORD_COPIED_TO_CLIPBOARD
      })
    );
  };

  return (
    <Box>
      <Typography
        data-testid="userPasswordTitle"
        gutterBottom
        variant="body1"
        className={classes.fontWeightBold}
      >
        {interpolateString([userName], MESSAGES.USER_PASSWORD_TEXT)}
      </Typography>
      <Typography
        data-testid="userPasswordSubTitle"
        gutterBottom
        variant="body1"
        className={classes.styler}
      >
        {MESSAGES.USER_PASSWORD_PARAGRAPH}
      </Typography>
      
      <Box className={classes.iconBox}>
        <Typography
        data-testid="userPasswordText"
        variant="body1"
        className={classes.divider}
        id="userPasswordText"
      >
        {displayUserPasswordTextHandler()}
      </Typography>
        <Tooltip
          arrow
          title={showUserPassword ? MESSAGES.HIDE_USER_PASSWORD : MESSAGES.SHOW_USER_PASSWORD}
          placement="bottom"
        >
            <span className={classes.iconAlignment}>
            {showUserPassword ? (
              <EyeOff data-testid="hideUserPasswordIcon" onClick={hideUserPasswordHandler} />
            ) : (
              <Eye data-testid="showUserPasswordIcon" onClick={showUserPasswordHandler} />
            )}
            </span>
        </Tooltip>
        <Tooltip arrow title={MESSAGES.COPY_TO_CLIPBOARD} placement="top" className={classes.iconAlignment}>
          <div>
            <CopyToClipboard data-testid="copyUserPasswordIcon" text={passwordText} onCopy={() => handleCopyClick()}>
              <Copy />
            </CopyToClipboard>
          </div>
        </Tooltip>
      </Box>
    </Box>
  );
};

export default UserPasswordDisplay;
